import { createImageUrlBuilder } from '@sanity/image-url';

import { SANITY_DATASET, SANITY_PROJECT_ID } from './config';
import type { SanityImageSource } from '@sanity/image-url';

// This module is bundled into the browser, so it builds URLs from the plain
// project coordinates rather than the token-bearing fetching client.
const builder = createImageUrlBuilder({
  projectId: SANITY_PROJECT_ID,
  dataset: SANITY_DATASET
});

export const buildSanityImageUrl = (source: SanityImageSource) =>
  builder.image(source).auto('format');

const SANITY_IMAGE_CDN_HOST = 'cdn.sanity.io';
const DEFAULT_TRANSFORM_QUALITY = 80;

/**
 * Builds a srcset from the Sanity CDN's URL-based resizing. Sourcing the
 * responsive variants from Sanity's CDN rather than next/image keeps them
 * off the Vercel optimiser (and its transform costs); it also kept these
 * images responsive while the optimiser was disabled site-wide (re-enabled
 * under FFH-1688). `fit=max` resizes down only, so a narrow upload is never
 * inflated to fill the larger breakpoints (FFH-2631).
 */
export const buildSanityImageSrcSet = (
  source: SanityImageSource,
  widths: number[],
  quality = DEFAULT_TRANSFORM_QUALITY
): string =>
  widths
    .map(
      (width) =>
        `${buildSanityImageUrl(source)
          .width(width)
          .fit('max')
          .quality(quality)
          .url()} ${width}w`
    )
    .join(', ');

/**
 * Applies the Sanity image pipeline (resize, quality, format negotiation) to
 * a raw asset URL string, for places where GROQ returns `asset->url` rather
 * than an image object `buildSanityImageUrl` can consume. Without these
 * parameters the CDN serves the original upload (multi-MB PNGs), which is
 * what drove the Sanity bandwidth spike in July 2026 (FFH-2065).
 *
 * Non-Sanity URLs (e.g. local /images fallbacks) pass through untouched.
 * `fit=max` resizes down only, never upscales.
 */
export const withSanityImageTransforms = (
  url: string,
  maxWidth: number,
  quality = DEFAULT_TRANSFORM_QUALITY
): string => {
  if (!url.includes(SANITY_IMAGE_CDN_HOST)) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}auto=format&fit=max&w=${maxWidth}&q=${quality}`;
};
